import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookmarkPlus, Search, SlidersHorizontal, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RangeSlider } from "@/components/ui/range-slider";
import { MultiSearchableSelect } from "@/components/ui/multi-searchable-select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { VoiceSearch } from "@/components/VoiceSearch";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { canUseVoiceSearch } from "@/lib/access";

interface LookupItem {
  id: number;
  name: string;
}

interface Bounds {
  min: number;
  max: number;
}

/** The full set of buyer-facing listing filters (sort is handled separately). */
export interface ListingFilterValues {
  search: string;
  categoryIds: string[];
  // SCRUM-90: multi-location filter — location filters are now multi-select
  // (mirrors categoryIds). Legacy single countryId/stateId/cityId values from
  // older saved filters are migrated to these arrays by normalizeLocationFilters.
  countryIds: string[];
  stateIds: string[];
  cityIds: string[];
  minPrice: string;
  maxPrice: string;
  minRevenue: string;
  maxRevenue: string;
  minEbitda: string;
  maxEbitda: string;
  // Valuation multiple = asking price / EBITDA, e.g. "3.5" (TKT-00155).
  minMultiple: string;
  maxMultiple: string;
  listedWithin: string; // "" | "30" | "90" | "180" | "365" (days)
  statusId: string; // admin-only; "" when the status select isn't shown
  agentId: string; // "" when the advisor select isn't shown (no advisorOptions)
}

export const EMPTY_FILTERS: ListingFilterValues = {
  // SCRUM-90: multi-location filter — countryIds/stateIds/cityIds replace the
  // former single countryId/stateId/cityId strings.
  search: "", categoryIds: [], countryIds: [], stateIds: [], cityIds: [],
  minPrice: "", maxPrice: "", minRevenue: "", maxRevenue: "", minEbitda: "", maxEbitda: "",
  minMultiple: "", maxMultiple: "",
  listedWithin: "", statusId: "", agentId: "",
};

/**
 * SCRUM-90: multi-location filter — coerce a possibly-legacy filter set (from
 * sessionStorage or a server-saved filter created before this feature) into the
 * current multi-select shape. Legacy single `countryId`/`stateId`/`cityId`
 * strings are promoted to single-element arrays so old saved filters keep
 * working. Removing this function + its call sites cleanly reverts the feature.
 * Module-local (not exported) — only readStoredFilters and the saved-filter
 * restore path below need it.
 */
function normalizeLocationFilters(
  v: Partial<ListingFilterValues> & { countryId?: string; stateId?: string; cityId?: string },
): ListingFilterValues {
  const merged = { ...EMPTY_FILTERS, ...v } as ListingFilterValues & {
    countryId?: string; stateId?: string; cityId?: string;
  };
  if (!Array.isArray(merged.categoryIds)) merged.categoryIds = [];
  const toArr = (arr: unknown, legacy: unknown): string[] =>
    Array.isArray(arr) && arr.length > 0
      ? (arr as string[])
      : typeof legacy === "string" && legacy
        ? [legacy]
        : [];
  merged.countryIds = toArr(v.countryIds, v.countryId);
  merged.stateIds = toArr(v.stateIds, v.stateId);
  merged.cityIds = toArr(v.cityIds, v.cityId);
  delete merged.countryId;
  delete merged.stateId;
  delete merged.cityId;
  return merged;
}

/**
 * Read a previously-persisted filter set from sessionStorage (written by
 * <ListingFilters storageKey=…>), merged over `fallback`. Lets a page seed its
 * own applied-filter state so returning from a listing detail restores filters,
 * not just the page. Returns `fallback` when nothing is stored or on error.
 */
export function readStoredFilters(key: string, fallback: ListingFilterValues): ListingFilterValues {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ListingFilterValues>;
    // SCRUM-90: multi-location filter — normalize legacy single-location fields
    // into the new arrays so filters persisted before this feature still load.
    return normalizeLocationFilters({ ...fallback, ...parsed });
  } catch {
    return fallback;
  }
}

/** Build the API query params for an applied filter set (shared by any consumer). */
export function buildListingFilterParams(v: ListingFilterValues): URLSearchParams {
  const p = new URLSearchParams();
  if (v.search) p.set("search", v.search);
  v.categoryIds.forEach((id) => p.append("industry_category_ids", id));
  // SCRUM-90: multi-location filter — emit repeatable *_ids params consumed by
  // the listing / admin / lead-pipeline endpoints. (`?? []` guards any legacy
  // object that reaches here without the new arrays.)
  (v.countryIds ?? []).forEach((id) => p.append("country_ids", id));
  (v.stateIds ?? []).forEach((id) => p.append("state_ids", id));
  (v.cityIds ?? []).forEach((id) => p.append("city_ids", id));
  if (v.minPrice) p.set("min_price", v.minPrice);
  if (v.maxPrice) p.set("max_price", v.maxPrice);
  if (v.minRevenue) p.set("min_revenue", v.minRevenue);
  if (v.maxRevenue) p.set("max_revenue", v.maxRevenue);
  if (v.minEbitda) p.set("min_ebitda", v.minEbitda);
  if (v.maxEbitda) p.set("max_ebitda", v.maxEbitda);
  if (v.minMultiple) p.set("min_multiple", v.minMultiple);
  if (v.maxMultiple) p.set("max_multiple", v.maxMultiple);
  if (v.listedWithin) p.set("listed_within_days", v.listedWithin);
  if (v.statusId) p.set("listing_status_id", v.statusId);
  if (v.agentId) p.set("agent_id", v.agentId);
  return p;
}

/**
 * Page state saved alongside a filter set — owned by the host page (e.g. the
 * marketplace's sort, status KPI and tab), restored via `onRestoreView`.
 */
export interface SavedFilterView {
  sortBy?: string;
  statusKpi?: number;
  marketTab?: "active" | "coming_soon" | "unlocks";
}

/** What /saved-filters stores in `filters`. */
export type SavedFilterPayload = ListingFilterValues & { view?: SavedFilterView };

/** A user-saved filter set (persisted server-side, re-applied from the sidebar). */
export interface SavedFilter {
  id: string;
  name: string;
  filters: SavedFilterPayload;
  created_at: string;
}

/** Rename a saved filter and tell every mounted sidebar / filter bar. */
export async function renameSavedFilter(id: string, name: string): Promise<void> {
  await api.patch<SavedFilter>(`/saved-filters/${id}`, { name });
  window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
}

/** Id → display-name resolvers used to build a saved filter's auto name. */
export interface FilterNameLookups {
  category: (id: string) => string;
  country: (id: string) => string;
  state: (id: string) => string;
  city: (id: string) => string;
  status: (id: string) => string;
  advisor: (id: string) => string;
}

/**
 * A short human-readable name for a filter set ("Pest Control · Texas · ≤$3M").
 * Pure so a saved filter's name can be rebuilt (and compared) outside the
 * component. Falls back when nothing meaningful is set.
 */
export function describeFilters(v: ListingFilterValues, L: FilterNameLookups): string {
  const parts: string[] = [];
  if (v.search) parts.push(`"${v.search}"`);
  if (v.categoryIds.length === 1) parts.push(L.category(v.categoryIds[0]));
  else if (v.categoryIds.length > 1) parts.push(`${v.categoryIds.length} categories`);
  // SCRUM-90: multi-location filter — summarize the most specific location set.
  if (v.cityIds.length === 1) parts.push(L.city(v.cityIds[0]));
  else if (v.cityIds.length > 1) parts.push(`${v.cityIds.length} cities`);
  else if (v.stateIds.length === 1) parts.push(L.state(v.stateIds[0]));
  else if (v.stateIds.length > 1) parts.push(`${v.stateIds.length} states`);
  else if (v.countryIds.length === 1) parts.push(L.country(v.countryIds[0]));
  else if (v.countryIds.length > 1) parts.push(`${v.countryIds.length} countries`);
  if (v.statusId) parts.push(L.status(v.statusId));
  if (v.agentId) parts.push(L.advisor(v.agentId));
  if (v.minPrice && v.maxPrice) parts.push(`${formatCompact(Number(v.minPrice))}–${formatCompact(Number(v.maxPrice))}`);
  else if (v.maxPrice) parts.push(`≤${formatCompact(Number(v.maxPrice))}`);
  else if (v.minPrice) parts.push(`≥${formatCompact(Number(v.minPrice))}`);
  if (v.minRevenue || v.maxRevenue) parts.push("revenue");
  if (v.minEbitda || v.maxEbitda) parts.push("EBITDA");
  if (v.minMultiple || v.maxMultiple) parts.push(multipleRangeLabel(v.minMultiple, v.maxMultiple));
  if (v.listedWithin) parts.push(PRESET_LABELS[v.listedWithin]);
  return parts.slice(0, 4).join(" · ") || "Untitled filter";
}

/** Max saved filters per user (must match the API's MAX_SAVED_FILTERS). */
export const MAX_SAVED_FILTERS = 3;

/** Window event fired after the saved-filter list changes, so sidebars can refresh. */
export const SAVED_FILTERS_CHANGED_EVENT = "saved-filters-changed";

/**
 * A removable active-filter chip. `key` must be unique and stable: labels are
 * NOT (two unresolved ids both read "Category: —"), and duplicate React keys
 * left ghost chips on screen after the ids were removed from state.
 */
interface ActiveChip {
  key: string;
  label: string;
  clear: () => void;
}

/** An option for the status (admin-only) / advisor selects. */
export interface FilterSelectOption {
  value: string;
  label: string;
}

const PRESET_LABELS: Record<string, string> = {
  "7": "Last 7 days", "30": "Last 30 days", "90": "Last 90 days", "180": "Last 6 months", "365": "Last year",
};

/** Fixed slider spans: every financial range runs $0 – $500M. */
const FILTER_BOUNDS: { price: Bounds; revenue: Bounds; ebitda: Bounds } = {
  price: { min: 0, max: 500_000_000 },
  revenue: { min: 0, max: 500_000_000 },
  ebitda: { min: 0, max: 500_000_000 },
};

/** Valuation multiple (asking price / EBITDA) slider span. Most deals trade
 *  between 2x and 8x; the top handle at 20x means "no maximum". */
const MULTIPLE_BOUNDS: Bounds = { min: 0, max: 20 };

const formatMultiple = (n: number) => `${Number(n.toFixed(1))}x`;

/** "2x–4x", "≤4x", "≥3x" — for the saved-filter name. */
function multipleRangeLabel(lo: string, hi: string): string {
  if (lo && hi) return `${formatMultiple(Number(lo))}–${formatMultiple(Number(hi))} multiple`;
  if (hi) return `≤${formatMultiple(Number(hi))} multiple`;
  return `≥${formatMultiple(Number(lo))} multiple`;
}

/** The slider number boxes are typed in $ millions ("2.5" = $2.5M). */
const MILLIONS = { unit: 1_000_000, prefix: "$", unitLabel: "M" } as const;

const formatPrice = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const formatCompact = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(n);

export interface ListingFiltersProps {
  /** Called with the applied filter set: debounced for live edits, on click for drawer "Apply". */
  onApply: (values: ListingFilterValues) => void;
  /** Debounce (ms) before emitting live quick-bar edits. Default 350. */
  debounceMs?: number;
  /** Stretch the quick bar to fill its container instead of centering at max-w-6xl. */
  /** Kept for callers that still pass it. Defaults to full width now — a
   *  centred filter bar inside a full-width page looked like a layout bug. */
  fullWidth?: boolean;
  /** Placeholder for the main search input. */
  searchPlaceholder?: string;
  /** Show price / revenue / EBITDA + "listed within" preset filters. Default true. */
  showFinancialFilters?: boolean;
  /** Override the outer section padding (e.g. when embedded in an already-padded shell). */
  containerClassName?: string;
  /** When provided, render an admin-only status select in the quick bar. */
  statusOptions?: FilterSelectOption[];
  /** Placeholder / "all" label for the status select. Default "All statuses". */
  statusPlaceholder?: string;
  /** Initial value for the status select (e.g. "3" to default to Active). */
  initialStatusId?: string;
  /** When provided, render a "filter by advisor" select. TKT-00129: buyers asked
   *  for this too, so it is no longer admin-only — it appears in the drawer at
   *  every width, plus the quick bar on large screens. */
  advisorOptions?: FilterSelectOption[];
  /** Placeholder / "all" label for the advisor select. Default "All advisors". */
  advisorPlaceholder?: string;
  /** Called after the user's saved-filter list changes (e.g. so a sidebar can refresh). */
  onSavedChange?: () => void;
  /**
   * When set, the applied filter set is persisted to sessionStorage under this
   * key and re-hydrated on mount, so navigating away (e.g. into a listing
   * detail) and back restores the filters. Pages should seed their own applied
   * state from the same key via `readStoredFilters`.
   */
  storageKey?: string;
  /**
   * Externally-driven category selection (e.g. the marketplace category
   * tiles). Each new `nonce` replaces the selected categories with `ids`, so
   * the pick shows up as a normal removable chip and is cleared by "Clear".
   */
  categoryPreset?: { ids: string[]; nonce: number };
  /** Host page state (sort / status / tab) stored with "Save this filter". */
  view?: SavedFilterView;
  /** Called whenever a saved filter is re-applied, with its stored `view` ({} if none). */
  onRestoreView?: (view: SavedFilterView) => void;
}

/**
 * Self-contained buyer listing filters: a quick bar (search + category + summary
 * pills) with removable active-filter chips, plus a right-side drawer for the full
 * set. Drawer edits are a draft until "Apply"; the quick bar applies live. Fetches
 * its own lookups, and emits the applied set via `onApply`.
 */
export function ListingFilters({
  onApply,
  debounceMs = 350,
  fullWidth = true,
  searchPlaceholder = "Search listings by keyword, industry, or description…",
  showFinancialFilters = true,
  containerClassName = "px-4 pt-6 sm:px-6 lg:px-8",
  statusOptions,
  statusPlaceholder = "All statuses",
  initialStatusId = "",
  advisorOptions,
  advisorPlaceholder = "All advisors",
  onSavedChange,
  storageKey,
  categoryPreset,
  view,
  onRestoreView,
}: ListingFiltersProps) {
  const toast = useToast();
  const { user } = useAuth();
  // AI voice filter is Operator-only (hidden for Basic + Explorer buyers).
  const voiceEnabled = canUseVoiceSearch(user);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial filter values: restored from sessionStorage when a storageKey is
  // given, otherwise the defaults (with the page's initial status).
  const initialFiltersRef = useRef<ListingFilterValues>(
    storageKey
      ? readStoredFilters(storageKey, { ...EMPTY_FILTERS, statusId: initialStatusId })
      : { ...EMPTY_FILTERS, statusId: initialStatusId },
  );
  const init0 = initialFiltersRef.current;
  // START: Default United States Selection
  // Capture — once, at mount, before the debounced emit effect can persist
  // anything — whether this filter set was already stored. Only a genuinely
  // fresh open (never persisted, or a page without persistence) is eligible for
  // the US default, so returning users (including those who cleared the country)
  // keep their choice.
  const usFreshLoadRef = useRef<boolean>(
    !storageKey || (() => { try { return sessionStorage.getItem(storageKey) === null; } catch { return true; } })(),
  );
  const usDefaultAppliedRef = useRef(false);
  // END: Default United States Selection
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [savingFilter, setSavingFilter] = useState(false);

  const [search, setSearch] = useState(init0.search);
  const [categoryIds, setCategoryIds] = useState<string[]>(init0.categoryIds);
  // SCRUM-90: multi-location filter — arrays of selected ids (were single strings).
  const [countryIds, setCountryIds] = useState<string[]>(init0.countryIds);
  const [stateIds, setStateIds] = useState<string[]>(init0.stateIds);
  const [cityIds, setCityIds] = useState<string[]>(init0.cityIds);
  const [minPrice, setMinPrice] = useState(init0.minPrice);
  const [maxPrice, setMaxPrice] = useState(init0.maxPrice);
  const [minRevenue, setMinRevenue] = useState(init0.minRevenue);
  const [maxRevenue, setMaxRevenue] = useState(init0.maxRevenue);
  const [minEbitda, setMinEbitda] = useState(init0.minEbitda);
  const [maxEbitda, setMaxEbitda] = useState(init0.maxEbitda);
  // `?? ""`: filter sets saved before the multiple existed don't carry it.
  const [minMultiple, setMinMultiple] = useState(init0.minMultiple ?? "");
  const [maxMultiple, setMaxMultiple] = useState(init0.maxMultiple ?? "");
  const [listedWithin, setListedWithin] = useState(init0.listedWithin);
  const [statusId, setStatusId] = useState(init0.statusId);
  const [agentId, setAgentId] = useState(init0.agentId);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const snapshotRef = useRef<ListingFilterValues | null>(null);

  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [states, setStates] = useState<LookupItem[]>([]);
  const [cities, setCities] = useState<LookupItem[]>([]);

  // Keep the latest onApply without retriggering the emit effect on every render.
  const onApplyRef = useRef(onApply);
  useEffect(() => { onApplyRef.current = onApply; });
  const onRestoreViewRef = useRef(onRestoreView);
  useEffect(() => { onRestoreViewRef.current = onRestoreView; });

  const collect = (): ListingFilterValues => ({
    // Under 3 chars is emitted as empty (mirrors the backend's
    // _min_search_length) — too short to mean anything as a keyword.
    // SCRUM-90: multi-location filter — emit location arrays.
    search: search.trim().length > 2 ? search : "",
    categoryIds, countryIds, stateIds, cityIds,
    minPrice, maxPrice, minRevenue, maxRevenue, minEbitda, maxEbitda, minMultiple, maxMultiple, listedWithin,
    statusId, agentId,
  });

  const apply = (s: ListingFilterValues) => {
    // SCRUM-90: multi-location filter — set location arrays.
    setSearch(s.search); setCategoryIds(s.categoryIds); setCountryIds(s.countryIds);
    setStateIds(s.stateIds); setCityIds(s.cityIds);
    setMinPrice(s.minPrice); setMaxPrice(s.maxPrice);
    setMinRevenue(s.minRevenue); setMaxRevenue(s.maxRevenue);
    setMinEbitda(s.minEbitda); setMaxEbitda(s.maxEbitda);
    setMinMultiple(s.minMultiple ?? ""); setMaxMultiple(s.maxMultiple ?? "");
    setListedWithin(s.listedWithin);
    setStatusId(s.statusId); setAgentId(s.agentId);
  };

  // Apply a category pick coming from outside the bar (only on a new nonce).
  const presetNonceRef = useRef(categoryPreset?.nonce);
  useEffect(() => {
    if (!categoryPreset || categoryPreset.nonce === presetNonceRef.current) return;
    presetNonceRef.current = categoryPreset.nonce;
    setCategoryIds(categoryPreset.ids);
  }, [categoryPreset]);

  // Lookups (once).
  useEffect(() => {
    api.get<LookupItem[]>("/lookups/categories").then(setCategories).catch(() => {});
    api.get<LookupItem[]>("/lookups/countries").then(setCountries).catch(() => {});
  }, []);

  // Drop category ids that are no longer pickable (retired via is_active, or
  // deleted) — from a saved filter or sessionStorage. They rendered as
  // "Category: —" chips the buyer couldn't manage in the picker, while still
  // being sent to the API. Skipped until the lookup has actually loaded.
  useEffect(() => {
    if (categories.length === 0) return;
    const known = new Set(categories.map((c) => String(c.id)));
    setCategoryIds((prev) => (prev.every((id) => known.has(id)) ? prev : prev.filter((id) => known.has(id))));
  }, [categories, categoryIds]);

  // SCRUM-90: multi-location filter — cascade for MULTIPLE countries. Load the
  // union of states for all picked countries in a single request (?country_ids=…),
  // then prune only the child selections that no longer belong to a picked
  // country. (Pruning instead of wiping keeps a restored saved filter's states,
  // and keeps the first country's states when a second country is added.)
  useEffect(() => {
    if (!countryIds.length) {
      setStates([]);
      setStateIds((prev) => (prev.length ? [] : prev));
      return;
    }
    let cancelled = false;
    const qs = countryIds.map((id) => `country_ids=${encodeURIComponent(id)}`).join("&");
    api.get<LookupItem[]>(`/lookups/states?${qs}`).then((list) => {
      if (cancelled) return;
      setStates(list);
      const valid = new Set(list.map((s) => String(s.id)));
      setStateIds((prev) => (prev.every((id) => valid.has(id)) ? prev : prev.filter((id) => valid.has(id))));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [countryIds]);

  // SCRUM-90: multi-location filter — cascade for MULTIPLE states → cities.
  useEffect(() => {
    if (!stateIds.length) {
      setCities([]);
      setCityIds((prev) => (prev.length ? [] : prev));
      return;
    }
    let cancelled = false;
    const qs = stateIds.map((id) => `state_ids=${encodeURIComponent(id)}`).join("&");
    api.get<LookupItem[]>(`/lookups/cities?${qs}`).then((list) => {
      if (cancelled) return;
      setCities(list);
      const valid = new Set(list.map((c) => String(c.id)));
      setCityIds((prev) => (prev.every((id) => valid.has(id)) ? prev : prev.filter((id) => valid.has(id))));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [stateIds]);

  // Emit applied filters (debounced). Suppressed while the drawer is open so its
  // edits stay a draft until "Apply" (which closes the drawer → fires this).
  useEffect(() => {
    if (filtersOpen) return;
    const t = setTimeout(() => {
      const v = collect();
      // Persist so returning from a listing detail restores the filters.
      if (storageKey) {
        try { sessionStorage.setItem(storageKey, JSON.stringify(v)); } catch { /* ignore */ }
      }
      onApplyRef.current(v);
    }, debounceMs);
    return () => clearTimeout(t);
    // SCRUM-90: multi-location filter — depend on location arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryIds, countryIds, stateIds, cityIds, minPrice, maxPrice, minRevenue, maxRevenue, minEbitda, maxEbitda, minMultiple, maxMultiple, listedWithin, statusId, agentId, filtersOpen, debounceMs, storageKey]);

  const openFilters = () => {
    snapshotRef.current = collect();
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    snapshotRef.current = null;
    setFiltersOpen(false);
  };

  const cancelFilters = () => {
    const s = snapshotRef.current;
    if (s) apply(s);
    snapshotRef.current = null;
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    apply(EMPTY_FILTERS);
  };

  const hasActiveFilters = Boolean(
    // SCRUM-90: multi-location filter — location arrays.
    search || categoryIds.length || countryIds.length || stateIds.length || cityIds.length ||
    minPrice || maxPrice || minRevenue || maxRevenue || minEbitda || maxEbitda || minMultiple || maxMultiple || listedWithin ||
    statusId || agentId,
  );

  // Close the drawer on Escape and lock background scroll while open.
  useEffect(() => {
    if (!filtersOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") cancelFilters(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersOpen]);

  const categoryName = (id: number) => categories.find((c) => c.id === id)?.name ?? "—";
  const countryNameById = (id: number) => countries.find((c) => c.id === id)?.name ?? "—";
  const stateNameById = (id: number) => states.find((s) => s.id === id)?.name ?? String(id);
  const cityNameById = (id: number) => cities.find((c) => c.id === id)?.name ?? String(id);

  const statusLabel = (id: string) => statusOptions?.find((o) => o.value === id)?.label ?? id;
  const advisorLabel = (id: string) => advisorOptions?.find((o) => o.value === id)?.label ?? id;

  // Saved filters are auto-named from the current set — no prompt.
  const autoFilterName = (): string =>
    describeFilters(collect(), {
      category: (id) => categoryName(Number(id)),
      country: (id) => countryNameById(Number(id)),
      state: (id) => stateNameById(Number(id)),
      city: (id) => cityNameById(Number(id)),
      status: statusLabel,
      advisor: advisorLabel,
    });

  const refreshSavedFilters = () =>
    api.get<SavedFilter[]>("/saved-filters/").then(setSavedFilters).catch(() => {});

  /**
   * Drop category ids that were retired/deleted since `saved` was stored, and
   * write the repaired set back so the sidebar stops advertising them. The
   * name is regenerated only while it is still the auto-generated one — a
   * name the user typed is left alone. Returns the filters to apply.
   */
  const healSavedFilter = async (saved: SavedFilter, v: ListingFilterValues): Promise<ListingFilterValues> => {
    if (v.categoryIds.length === 0) return v;
    const all = await api.get<(LookupItem & { is_active?: boolean })[]>("/lookups/categories?include_inactive=true");
    if (all.length === 0) return v;
    const active = new Set(all.filter((c) => c.is_active !== false).map((c) => String(c.id)));
    const kept = v.categoryIds.filter((id) => active.has(id));
    if (kept.length === v.categoryIds.length) return v;
    const healed = { ...v, categoryIds: kept };

    // Only the names the label actually shows (exactly one state/city) need a lookup.
    const [countryList, stateList, cityList] = await Promise.all([
      v.countryIds.length === 1 ? api.get<LookupItem[]>("/lookups/countries") : Promise.resolve([] as LookupItem[]),
      v.stateIds.length === 1 && v.countryIds.length
        ? api.get<LookupItem[]>(`/lookups/states?${v.countryIds.map((id) => `country_ids=${id}`).join("&")}`)
        : Promise.resolve([] as LookupItem[]),
      v.cityIds.length === 1
        ? api.get<LookupItem[]>(`/lookups/cities?${v.stateIds.map((id) => `state_ids=${id}`).join("&")}`)
        : Promise.resolve([] as LookupItem[]),
    ]);
    const byId = (list: LookupItem[], fallback: (id: string) => string) => (id: string) =>
      list.find((x) => String(x.id) === id)?.name ?? fallback(id);
    const lookups: FilterNameLookups = {
      category: byId(all, () => "—"),
      country: byId(countryList, () => "—"),
      state: byId(stateList, String),
      city: byId(cityList, String),
      status: statusLabel,
      advisor: advisorLabel,
    };
    const wasAutoNamed = saved.name === describeFilters(v, lookups);
    const payload: { filters: SavedFilterPayload; name?: string } = {
      filters: { ...healed, ...(saved.filters.view ? { view: saved.filters.view } : {}) },
    };
    if (wasAutoNamed) payload.name = describeFilters(healed, lookups);
    try {
      await api.patch<SavedFilter>(`/saved-filters/${saved.id}`, payload);
      window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
    } catch { /* still apply the cleaned set locally */ }
    const dropped = v.categoryIds.length - kept.length;
    toast.success(`${dropped} ${dropped === 1 ? "category is" : "categories are"} no longer available and ${dropped === 1 ? "was" : "were"} removed from this filter.`);
    return healed;
  };

  const saveCurrentFilter = async () => {
    if (savedFilters.length >= MAX_SAVED_FILTERS) {
      toast.error(`You can save up to ${MAX_SAVED_FILTERS} filters. Delete one first.`);
      return;
    }
    setSavingFilter(true);
    try {
      const filters: SavedFilterPayload = { ...collect(), ...(view ? { view } : {}) };
      await api.post<SavedFilter>("/saved-filters/", { name: autoFilterName(), filters });
      await refreshSavedFilters();
      onSavedChange?.();
      // Let the (persistent) sidebars refresh their Saved Filters section live.
      window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
      toast.success("Filter saved");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not save filter");
    } finally {
      setSavingFilter(false);
    }
  };

  // Load the user's saved filters (drives the Save button's count/disabled), and
  // keep it fresh when saved filters change elsewhere (e.g. deleted from a sidebar).
  useEffect(() => {
    refreshSavedFilters();
    window.addEventListener(SAVED_FILTERS_CHANGED_EVENT, refreshSavedFilters);
    return () => window.removeEventListener(SAVED_FILTERS_CHANGED_EVENT, refreshSavedFilters);
  }, []);

  // Auto-apply when the sidebar links here with ?savedFilter=<id>. Re-runs whenever
  // the param changes, so re-clicking another saved filter while already on the page
  // still applies. The location cascade prunes rather than wipes, so the saved
  // country/state/city survive (and their lookups load); the param is then cleared.
  const savedFilterParam = searchParams.get("savedFilter");
  useEffect(() => {
    if (!savedFilterParam) return;
    let cancelled = false;
    (async () => {
      const list = await api.get<SavedFilter[]>("/saved-filters/");
      if (cancelled) return;
      const match = list.find((f) => f.id === savedFilterParam);
      if (match) {
        // A saved filter is an explicit selection: never let the US default
        // (which may still be waiting on the country lookup) overwrite a saved
        // "any country".
        usDefaultAppliedRef.current = true;
        // SCRUM-90: multi-location filter — migrate legacy single-location saved filters.
        const { view, ...rest } = match.filters;
        let values = normalizeLocationFilters({ ...EMPTY_FILTERS, ...rest });
        try { values = await healSavedFilter(match, values); } catch { /* apply as saved */ }
        if (cancelled) return;
        apply(values);
        // Always called (filters saved before `view` existed pass {}), so the
        // host can also bring its results into view.
        onRestoreViewRef.current?.(view ?? {});
      }
      const next = new URLSearchParams(searchParams);
      next.delete("savedFilter");
      setSearchParams(next, { replace: true });
    })().catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterParam]);

  // START: Default United States Selection
  // On a pristine first open, preselect "United States" in the Country filter so
  // States load automatically via the existing country→state cascade (no change
  // to that logic). Runs at most once, only after countries have loaded, and is
  // skipped whenever a real selection already exists — a deep-linked saved filter
  // (?savedFilter=), a filter set restored from storage, back-navigation, or any
  // manual choice — so saved filters and existing behavior are preserved.
  useEffect(() => {
    if (countries.length === 0) return;            // wait for the country list
    if (usDefaultAppliedRef.current) return;        // apply at most once per mount
    usDefaultAppliedRef.current = true;
    if (savedFilterParam) return;                   // deep-linking a saved filter
    if (!usFreshLoadRef.current) return;            // returning user — respect stored state
    if (init0.countryIds.length > 0) return;        // something was restored at mount
    if (countryIds.length > 0) return;              // a country is already selected
    const us = countries.find((c) => c.name === "United States");
    if (!us) return;                                // invalid/missing → graceful no-op
    // Selecting the country reuses the existing cascade effect to fetch states.
    setCountryIds([String(us.id)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, savedFilterParam]);
  // END: Default United States Selection

  const activeFilters: ActiveChip[] = [
    search && { key: "search", label: `Query: ${search}`, clear: () => setSearch("") },
    statusId && { key: "status", label: `Status: ${statusLabel(statusId)}`, clear: () => setStatusId("") },
    agentId && { key: "agent", label: `Advisor: ${advisorLabel(agentId)}`, clear: () => setAgentId("") },
    ...categoryIds.map((id) => ({
      key: `category:${id}`,
      label: `Category: ${categoryName(Number(id))}`,
      clear: () => setCategoryIds((prev) => prev.filter((x) => x !== id)),
    })),
    // SCRUM-90: multi-location filter — one removable chip per selected location.
    ...countryIds.map((id) => ({
      key: `country:${id}`,
      label: `Country: ${countryNameById(Number(id))}`,
      clear: () => setCountryIds((prev) => prev.filter((x) => x !== id)),
    })),
    ...stateIds.map((id) => ({
      key: `state:${id}`,
      label: `State: ${stateNameById(Number(id))}`,
      clear: () => setStateIds((prev) => prev.filter((x) => x !== id)),
    })),
    ...cityIds.map((id) => ({
      key: `city:${id}`,
      label: `City: ${cityNameById(Number(id))}`,
      clear: () => setCityIds((prev) => prev.filter((x) => x !== id)),
    })),
    minPrice && { key: "minPrice", label: `Min price ${formatPrice(Number(minPrice))}`, clear: () => setMinPrice("") },
    maxPrice && { key: "maxPrice", label: `Max price ${formatPrice(Number(maxPrice))}`, clear: () => setMaxPrice("") },
    minRevenue && { key: "minRevenue", label: `Min revenue ${formatPrice(Number(minRevenue))}`, clear: () => setMinRevenue("") },
    maxRevenue && { key: "maxRevenue", label: `Max revenue ${formatPrice(Number(maxRevenue))}`, clear: () => setMaxRevenue("") },
    minEbitda && { key: "minEbitda", label: `Min EBITDA ${formatPrice(Number(minEbitda))}`, clear: () => setMinEbitda("") },
    maxEbitda && { key: "maxEbitda", label: `Max EBITDA ${formatPrice(Number(maxEbitda))}`, clear: () => setMaxEbitda("") },
    minMultiple && { key: "minMultiple", label: `Min multiple ${formatMultiple(Number(minMultiple))}`, clear: () => setMinMultiple("") },
    maxMultiple && { key: "maxMultiple", label: `Max multiple ${formatMultiple(Number(maxMultiple))}`, clear: () => setMaxMultiple("") },
    listedWithin && { key: "listedWithin", label: `Listed: ${PRESET_LABELS[listedWithin]}`, clear: () => setListedWithin("") },
  ].filter(Boolean) as ActiveChip[];

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  // SCRUM-90: multi-location filter — options for the location multi-selects.
  const countryOptions = countries.map((c) => ({ value: String(c.id), label: c.name }));
  const stateOptions = states.map((s) => ({ value: String(s.id), label: s.name }));
  const cityOptions = cities.map((c) => ({ value: String(c.id), label: c.name }));

  return (
    <>
      <section className={containerClassName}>
        <div className={`public-glass-card ${fullWidth ? "w-full" : "mx-auto max-w-6xl"} rounded-2xl shadow-glow overflow-hidden`}>
          {/* Primary search bar */}
          <form onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
            {/* gap-2 on the row, not per-control margins: VoiceSearch renders
                without any of its own, so a gap-0 row left it touching the
                status select. Controls below keep my-2 for height only. */}
            {/* Phones: the input takes its own line and the controls wrap onto
                a second one — a single nowrap row pushed Filters/Search past
                the card's overflow-hidden edge. sm+ is one row as before. */}
            <div className="flex flex-wrap items-center gap-x-2 border-b border-border/70 sm:flex-nowrap sm:pr-2">
              <div className="flex min-w-0 basis-full items-center sm:flex-1 sm:basis-auto">
                <Search className="ml-5 h-5 w-5 shrink-0 text-muted-foreground/50" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-14 min-w-0 flex-1 bg-transparent px-4 text-base text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
                />
              </div>
              <div className="flex w-full flex-wrap items-center justify-end gap-2 px-3 sm:w-auto sm:flex-nowrap sm:px-0">
              {voiceEnabled && <VoiceSearch onSearch={setSearch} disabled={false} />}
              {statusOptions && (
                <select
                  value={statusId}
                  onChange={(e) => setStatusId(e.target.value)}
                  aria-label="Status"
                  className="my-2 h-10 rounded-lg border border-border/70 bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                >
                  <option value="">{statusPlaceholder}</option>
                  {statusOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {advisorOptions && (
                <div className="my-2 hidden w-44 lg:block">
                  <SearchableSelect
                    value={agentId}
                    onChange={setAgentId}
                    options={advisorOptions}
                    placeholder={advisorPlaceholder}
                    className="h-10"
                  />
                </div>
              )}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear
                </button>
              )}
              <Button type="button" variant="outline" onClick={openFilters} className="relative my-2 h-10 gap-2 px-4">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFilters.length > 0 && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[0.65rem] font-bold text-primary-foreground">
                    {activeFilters.length}
                  </span>
                )}
              </Button>
              <Button type="submit" className="my-2 h-10 px-5 rounded-xl">
                <Search className="h-4 w-4" />
                Search
              </Button>
              </div>
            </div>
          </form>

          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-4 py-3">
              <span className="text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Active filters:</span>
              {activeFilters.map((f) => (
                <span key={f.key} className="inline-flex items-center gap-1 rounded-full border border-primary/15 bg-primary/10 py-1 pl-2.5 pr-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-primary">
                  {f.label}
                  <button type="button" onClick={f.clear} aria-label={`Remove ${f.label}`} className="rounded-full p-0.5 hover:bg-primary/15">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Right-side drawer — edits stay a draft until "Apply". */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={cancelFilters} />
          <aside className="relative z-10 flex h-full w-full flex-col bg-card shadow-2xl sm:w-[clamp(20rem,26vw,26rem)]">
            <header className="flex items-center justify-between border-b border-border/70 px-5 py-4">
              <h3 className="text-lg font-bold text-foreground">Filters</h3>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearFilters} className="text-xs font-semibold text-primary hover:underline">
                  Clear all
                </button>
                <button type="button" onClick={cancelFilters} aria-label="Close filters" className="rounded-md p-1 text-muted-foreground hover:bg-accent">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </header>

            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <div className="space-y-1.5">
                <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Keyword search</Label>
                <div className="flex gap-2 items-center">
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings…" className="h-9 text-sm flex-1" />
                  {voiceEnabled && <VoiceSearch onSearch={setSearch} disabled={false} />}
                </div>
              </div>

              {showFinancialFilters && (
                <div className="space-y-1.5">
                  <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Preset ranges</Label>
                  <div className="flex flex-wrap gap-2">
                    {([["", "Any"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["180", "Last 6 months"], ["365", "Last year"]] as const).map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setListedWithin(v)}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                          listedWithin === v ? "border-primary bg-primary text-primary-foreground" : "border-border/70 text-foreground hover:bg-accent",
                        )}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Category</Label>
                <MultiSearchableSelect values={categoryIds} onChange={setCategoryIds} placeholder="All categories" options={categoryOptions} />
              </div>

              {/* TKT-00129: filter by advisor. Same `agentId` state as the
                  quick-bar select above, which only shows from lg up — this is
                  the one place the filter is reachable on a phone. */}
              {advisorOptions && (
                <div className="space-y-1.5">
                  <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Advisor</Label>
                  <SearchableSelect value={agentId} onChange={setAgentId} options={advisorOptions} placeholder={advisorPlaceholder} />
                </div>
              )}

              {showFinancialFilters && (
                <>
                  <RangeSlider label="Asking price" bound={FILTER_BOUNDS.price} valueMin={minPrice} valueMax={maxPrice} onChange={(lo, hi) => { setMinPrice(lo); setMaxPrice(hi); }} format={formatCompact} {...MILLIONS} />
                  <RangeSlider label="Revenue" bound={FILTER_BOUNDS.revenue} valueMin={minRevenue} valueMax={maxRevenue} onChange={(lo, hi) => { setMinRevenue(lo); setMaxRevenue(hi); }} format={formatCompact} {...MILLIONS} />
                  <RangeSlider label="EBITDA" bound={FILTER_BOUNDS.ebitda} valueMin={minEbitda} valueMax={maxEbitda} onChange={(lo, hi) => { setMinEbitda(lo); setMaxEbitda(hi); }} format={formatCompact} {...MILLIONS} />
                  {/* Asking price ÷ EBITDA. A gentler curve than the $ sliders so
                      the 2x–8x band where most deals sit gets most of the track. */}
                  <div className="space-y-1">
                    <RangeSlider
                      label="Multiple (price ÷ EBITDA)"
                      bound={MULTIPLE_BOUNDS}
                      valueMin={minMultiple}
                      valueMax={maxMultiple}
                      onChange={(lo, hi) => { setMinMultiple(lo); setMaxMultiple(hi); }}
                      format={(n) => (n >= MULTIPLE_BOUNDS.max ? `${MULTIPLE_BOUNDS.max}x+` : formatMultiple(n))}
                      unitLabel="x"
                      curve={1.6}
                    />
                    {(minMultiple || maxMultiple) && (
                      <p className="text-[0.7rem] leading-snug text-muted-foreground">
                        Only listings with an asking price and positive EBITDA have a multiple.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* SCRUM-90: multi-location filter — Country/State/City are now
                  multi-selects using the same control as Category. Cascade is
                  preserved: State is disabled until a Country is picked, City
                  until a State is picked. */}
              <div className="space-y-1.5">
                <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Country</Label>
                <MultiSearchableSelect values={countryIds} onChange={setCountryIds} placeholder="All countries" options={countryOptions} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">State</Label>
                <MultiSearchableSelect values={stateIds} onChange={setStateIds} placeholder="All states" options={stateOptions} disabled={countryIds.length === 0} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">City</Label>
                <MultiSearchableSelect values={cityIds} onChange={setCityIds} placeholder="All cities" options={cityOptions} disabled={stateIds.length === 0} />
              </div>
            </div>

            <footer className="space-y-2 border-t border-border/70 p-4">
              <Button
                type="button"
                variant="outline"
                onClick={saveCurrentFilter}
                disabled={!hasActiveFilters || savingFilter || savedFilters.length >= MAX_SAVED_FILTERS}
                className="h-10 w-full gap-2 rounded-xl text-sm"
              >
                <BookmarkPlus className="h-4 w-4" />
                {savedFilters.length >= MAX_SAVED_FILTERS
                  ? `Saved filters full (${MAX_SAVED_FILTERS}/${MAX_SAVED_FILTERS})`
                  : `Save this filter (${savedFilters.length}/${MAX_SAVED_FILTERS})`}
              </Button>
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" onClick={cancelFilters} className="h-11 flex-1 rounded-xl text-base">
                  Cancel
                </Button>
                <Button type="button" onClick={applyFilters} className="h-11 flex-1 rounded-xl text-base">
                  Apply filters
                </Button>
              </div>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}
